import { useApolloClient } from '@apollo/client';
import { usePropertyContext } from 'context/propertyContext';
import {
	useBatchSaveUserSettingsMutation,
	useGetUserSettingsQuery,
	useSaveUserSettingMutation,
} from 'features/my-view/gql/_gen_/my-metrics.gql';
import { useUser } from 'features/users/context/userContext';
import { SettingsScope } from 'graphql/gql-types';
import { useCallback, useMemo } from 'react';

/**
 * Type for user settings values
 */
export type SettingValue = string | number | boolean | object | null;

/**
 * Hook for managing user settings with database persistence and scope support
 * Supports both property-specific and universal settings
 */
export function useUserSettings(scope: SettingsScope = SettingsScope.property) {
	const { property } = usePropertyContext();
	const { user } = useUser();
	const client = useApolloClient();

	const propertyId =
		scope === SettingsScope.property ? property?.propertyId : undefined;
	const userId = user?.id;
	const isPropertyScope = scope === SettingsScope.property;
	const missingPropertyForScope = isPropertyScope && !propertyId;

	// Query for user settings
	const {
		data: settingsData,
		loading: settingsLoading,
		error: settingsError,
		refetch: refetchSettings,
	} = useGetUserSettingsQuery({
		variables: {
			userId: userId || '',
			propertyId,
			scope,
		},
		skip: !userId || missingPropertyForScope,
		fetchPolicy: 'cache-and-network', // Use both cache and network for optimal performance
		errorPolicy: 'all', // Don't crash on errors, allow fallbacks
	});

	// Mutation for saving individual settings
	const [saveSettingMutation] = useSaveUserSettingMutation({
		onCompleted: () => {
			// Refetch to ensure cache consistency
			refetchSettings();
		},
		onError: (error) => {
			console.error('Failed to save user setting:', error);
		},
	});

	// Mutation for batch saving multiple settings
	const [batchSaveSettingsMutation] = useBatchSaveUserSettingsMutation({
		onCompleted: () => {
			// Refetch to ensure cache consistency
			refetchSettings();
		},
		onError: (error) => {
			console.error('Failed to batch save user settings:', error);
		},
	});

	// Parse settings from JSON string to object
	const settings = useMemo(() => {
		const userSettings = settingsData?.getUserSettings;
		const isScopeMatch = !userSettings?.scope || userSettings.scope === scope;
		const isPropertyMatch =
			scope === SettingsScope.universal ||
			!propertyId ||
			userSettings?.property_id === propertyId;

		if (!isScopeMatch || !isPropertyMatch) {
			// Ignore stale cached data from a different scope/property while the next query resolves.
			return {};
		}

		const rawSettings = settingsData?.getUserSettings?.settings;
		try {
			if (!rawSettings) return {};
			if (typeof rawSettings === 'string') {
				return JSON.parse(rawSettings);
			}
			if (typeof rawSettings === 'object') {
				return rawSettings;
			}
			return {};
		} catch (error) {
			console.error('Failed to parse user settings:', error);
			return {};
		}
	}, [settingsData?.getUserSettings, scope, propertyId]);

	const serializeForAwsJson = useCallback((value: unknown): string => {
		return JSON.stringify(value);
	}, []);

	// Get a specific setting value with fallback
	const getSetting = useCallback(
		(key: string, fallback?: SettingValue): SettingValue => {
			if (settings && settings[key] !== undefined) {
				return settings[key];
			}
			return fallback ?? null;
		},
		[settings],
	);

	// Save a single setting
	const setSetting = useCallback(
		async (key: string, value: SettingValue): Promise<void> => {
			if (!userId) {
				throw new Error('User not authenticated');
			}

			if (isPropertyScope && !propertyId) {
				throw new Error('Property not selected for property-scoped setting');
			}

			try {
				const result = await saveSettingMutation({
					variables: {
						userId,
						propertyId,
						key,
						value: serializeForAwsJson(value),
						scope,
					},
				});

				if (!result?.data?.saveUserSetting) {
					const graphQLErrorMessage =
						result.errors?.[0]?.message || 'No GraphQL error details returned';
					const details = {
						userId,
						propertyId,
						scope,
						key,
						graphQLErrorMessage,
						graphqlErrors: result.errors,
					};
					console.warn('saveUserSetting returned null response', details);
					throw new Error(
						`saveUserSetting returned null response for key=${key}, scope=${scope}. ${graphQLErrorMessage}`,
					);
				}
			} catch (error) {
				console.error(`Failed to save setting ${key}:`, error);
				throw error;
			}
		},
		[
			userId,
			propertyId,
			scope,
			isPropertyScope,
			saveSettingMutation,
			serializeForAwsJson,
		],
	);

	// Save multiple settings at once (batch operation)
	const setSettings = useCallback(
		async (settingsToSave: Record<string, SettingValue>): Promise<void> => {
			if (!userId) {
				throw new Error('User not authenticated');
			}

			if (isPropertyScope && !propertyId) {
				throw new Error('Property not selected for property-scoped settings');
			}

			try {
				const result = await batchSaveSettingsMutation({
					variables: {
						userId,
						propertyId,
						settings: serializeForAwsJson(settingsToSave),
						scope,
					},
				});

				if (!result?.data?.batchSaveUserSettings) {
					const graphQLErrorMessage =
						result.errors?.[0]?.message || 'No GraphQL error details returned';
					const details = {
						userId,
						propertyId,
						scope,
						keys: Object.keys(settingsToSave),
						graphQLErrorMessage,
						graphqlErrors: result.errors,
					};
					console.warn('batchSaveUserSettings returned null response', details);
					throw new Error(
						`batchSaveUserSettings returned null response for scope=${scope}. ${graphQLErrorMessage}`,
					);
				}
			} catch (error) {
				console.error('Failed to batch save settings:', error);
				throw error;
			}
		},
		[
			userId,
			propertyId,
			scope,
			isPropertyScope,
			batchSaveSettingsMutation,
			serializeForAwsJson,
		],
	);

	// Clear all cached settings (useful for scope switching)
	const clearCache = useCallback(() => {
		client.cache.evict({
			fieldName: 'getUserSettings',
			args: { userId, propertyId, scope },
		});
		client.cache.gc();
	}, [client, userId, propertyId, scope]);

	return {
		// Settings data
		settings,
		settingsLoading,
		settingsError,

		// Individual setting operations
		getSetting,
		setSetting,

		// Batch operations
		setSettings,

		// Cache management
		clearCache,
		refetchSettings,

		// Metadata
		scope,
		isReady: !settingsLoading && !missingPropertyForScope,
	};
}

/**
 * Hook specifically for property-scoped settings
 */
export function usePropertySettings() {
	return useUserSettings(SettingsScope.property);
}

/**
 * Hook specifically for universal settings (shared across all properties)
 */
export function useUniversalSettings() {
	return useUserSettings(SettingsScope.universal);
}
